import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ISiteTrial, TrialStatus } from '~src/siteManagement/serverApi/types/ISiteTrial';
import { IGroupBasicInfo } from '~src/userManagement/groups/models/IGroupInfo';

import { Agent, AgentPerm } from './AgentSectionTypes';

export const getTestId = (component: string, key?: string) => `settings_agents_${component}${key ? `_${key}` : ''}`;

// Whether an agent's TYPE supports group-based access. This is a static capability check:
// the agent must declare a group setting (groupIdSiteSetting != null — agents like
// tableauKnowledge opt out with null) AND its group feature flags must allow groups
// (readyForGroupsFlag on/absent, dashboardQAFlag not forcing groups off). Runtime state
// (enabled, availability, trial mode) is layered on by callers, not here. Shared by the
// action menu (grantAccess option) and the row body (group column / icon) so the flag
// logic lives in one place.
export const agentSupportsGroups = (agent: Agent): boolean =>
    agent.groupIdSiteSetting != null
    && (!agent.readyForGroupsFlag || !!FeatureFlags[agent.readyForGroupsFlag])
    && !(agent.dashboardQAFlag && FeatureFlags[agent.dashboardQAFlag]);

// Composes an agent's rendered label: "<displayName>: <description>" via the locale-aware
// messageWithColonSeparator. Every agent carries both getters (see Agent), so the composition
// is unconditional. The '*' is a runtime trial marker (not translatable), shown on Trial sites
// for registration-gated agents in the trial (requiresRegistration && availableInTrial).
export const getAgentLabel = (agent: Agent, isTrial: boolean): string => {
    const showTrialMarker = isTrial && agent.requiresRegistration && agent.availableInTrial;
    const label = Messages.messageWithColonSeparator({ MESSAGE_PART_1: agent.displayName(), MESSAGE_PART_2: agent.description() });
    return showTrialMarker ? `*${label}` : label;
};

// Resolves the Formik field path for an agent's enabled/groupId setting. Legacy
// (siteAdminSettings) settings are flattened into `settings.*`; V2 settings stay
// nested under `settings.siteSettingsV2.*`. Keeps AgentRow namespace-agnostic.
export const getAgentFieldPath = (agent: Agent, perm: AgentPerm): string =>
    agent.settingsNamespace ? `settings.${agent.settingsNamespace}.${agent[perm]}` : `settings.${agent[perm]}`;

export const _groupName = (groups: IGroupBasicInfo[]) => (id: string) => {
    const group = groups.find(g => g.groupLuid === id);
    return group?.name ?? group?.groupLuid ?? Messages.settings_agents_invalid_group();
};

export type AgentAvailabilityInfo = {
    actionButtonDisabled: boolean;
    isAvailable: boolean;
}

// Whether an agent's feature is available under the Tableau AI trial in try-it mode.
//
// Matching is driven by `restartFeatures`, NOT `onboardedFeatures`: per the getTrials contract
// (see W-21711350 restart-trial design) `onboardedFeatures` holds raw YAML DB keys (e.g.
// `generativeaiPulseDiscover`) that do not match `agent.enabledSiteSetting`, while `restartFeatures`
// holds serialized names that do. `restartFeatures` is defined as `registry − onboarded`, and the
// client's copy of "feature is in the trial registry" is the static `availableInTableauAITrial`
// flag — so `onboarded = registry − restartFeatures` is derivable without touching the raw
// `onboardedFeatures` list at all.
//
// - Pre-trial (no trial object): fall back to the static registry flag.
// - Not in the trial registry: never available.
// - In the registry and the trial is restartable: available (it is either onboarded now or would
//   be onboarded on restart — both eligible).
// - In the registry and not restartable: available only if onboarded, i.e. NOT in restartFeatures.
export const isAgentInTableauAITrial = (agent: Agent, tableauAITrial?: ISiteTrial): boolean => {
    if (!tableauAITrial) {
        return agent.availableInTableauAITrial;
    }
    if (!agent.availableInTableauAITrial) {
        return false;
    }
    if (tableauAITrial.canRestart) {
        return true;
    }
    const restartFeatures = tableauAITrial.restartFeatures ?? [];
    return restartFeatures.indexOf(agent.enabledSiteSetting) === -1;
};

export const getAgentAvailabilityInfo = ({
    agent,
    allowTestRegistration,
    isTrial,
    isTryItMode,
    registered,
    tableauAITrial,
}: {
    agent: Agent;
    allowTestRegistration: boolean;
    isTrial: boolean;
    isTryItMode: boolean;
    registered: boolean;
    tableauAITrial?: ISiteTrial;
}): AgentAvailabilityInfo => {
    let isAvailable = registered || !agent.requiresRegistration || (isTrial && agent.availableInTrial);
    let actionButtonDisabled = !isAvailable;

    if (!isAvailable) {
        if (isTryItMode && isAgentInTableauAITrial(agent, tableauAITrial)) {
            isAvailable = true;
            actionButtonDisabled = !tableauAITrial || tableauAITrial.status === TrialStatus.EXPIRED;
        } else if (!isTryItMode && allowTestRegistration) {
            isAvailable = true;
            actionButtonDisabled = false;
        }
    }

    return { actionButtonDisabled, isAvailable };
};
